import { ReactNode } from "react";

import { MessageDescriptor } from "react-intl";

import { LinkModalProps } from "@/patterns/modal/src/link-modal";

//TODO: Rewrite mediaQueries for clarity (i.e tablet v desktop seem too similar)
export const deviceMediaQueries = {
    desktop: `@media (min-width: 1024px)`,
    tablet: `@media (max-width: 1023px)`,
    mobile: `@media (max-width: 768px)`,
    extraSmall: `@media (max-width: 360px)`
};

export const TRACKING_EVENT_NAMES = {
    unitTierBox: "pricing_page_unit_tier_box",
    unitDescriptionTooltipTriggered: "unit-description-tooltip-triggered",
    billingCycleChanged: "billing-cycle-changed",
    planCardCTAButtonClicked: "edition-table-button-clicked",
    unitBreachModalDecision: "unit-breach-modal-decision",
    unitLimitBreachModal: "unit-limit-breach-modal",
    userTierTooltipTriggered: "user-tier-tooltip-triggered",
    showPricingExampleTriggered: "show-pricing-example-triggered",
    hidePricingExampleTriggered: "hide-pricing-example-triggered",
    pricingExampleBillingCycleChanged: "pricing-example-billing-cycle-changed",
    promoCardCTAButtonClicked: "promo-card-button-clicked"
} as const;

export type TrackingEvent = keyof typeof TRACKING_EVENT_NAMES;

export type TrackingEventValues = (typeof TRACKING_EVENT_NAMES)[TrackingEvent];

export const UNIT_TRACKING_TERMS = {
    users: "users",
    agents: "agents"
} as const;

export type CurrencyISOCode = "USD" | "JPY";

export const DEFAULT_COUNTRY_ISO_CODE = "US";
export const DEFAULT_CURRENCY_ISO_CODE = "USD";

export const SpecialPlanNames = {
    FREE: "Free",
    STARTER: "Starter",
    STANDARD: "Standard",
    PREMIUM: "Premium",
    ENTERPRISE: "Enterprise"
} as const;

export type SpecialPlanNames = keyof typeof SpecialPlanNames;

export type ProductPlanNames =
    | (typeof SpecialPlanNames)[SpecialPlanNames]
    | string;

export const CloudProductsWithProductKeys = {
    confluence: "pricingplan.confluence.ondemand",
    jiraSoftware: "jira-software.ondemand",
    jiraServiceDesk: "jira-servicedesk.ondemand",
    jiraServiceManagement: "jira-servicedesk.ondemand",
    bitbucket: "com.atlassian.bitbucket.manual",
    atlassianGuard: "com.atlassian.identity-manager",
    compass: "compass.ondemand",
    jiraProductDiscovery: "jira-product-discovery.ondemand",
    rovo: "rovo",
    trello: "trello",
    loom: "loom",
    teamworkCollection: "teamwork-collection",
    strategyCollection: "strategy-collection"
};

export const CloudOfferingsWithSkus = {
    confluence: "confluence",
    jiraSoftware: "jira-software",
    jiraServiceDesk: "jira-servicedesk",
    jiraServiceManagement: "jira-servicedesk",
    atlassianGuard: "atlassian-access",
    compass: "compass",
    jiraProductDiscovery: "jira-product-discovery",
    rovo: "rovo-org-purchase-sku",
    trello: "trello",
    loom: "loom"
};

export const DataCenterProducts = {
    confluence: "pricingplan.confluence-data-center",
    jiraSoftware: "jira-software.data-center",
    jiraServiceDesk: "jira-servicedesk.data-center",
    jiraServiceManagement: "jira-servicedesk.data-center",
    bitbucket: "pricingplan.stash-data-center"
};

export type CloudProducts = typeof CloudProductsWithProductKeys;
export type CloudProductKey = keyof CloudProducts;
export type CloudProductValues = CloudProducts[CloudProductKey];
export type CloudOfferings = typeof CloudOfferingsWithSkus;
export type CloudOfferingKey = keyof CloudOfferings;
export type CloudOfferingValues = CloudOfferings[CloudOfferingKey];
export type DataCenterProducts = typeof DataCenterProducts;
export type DataCenterProductKey = keyof DataCenterProducts;
export type DataCenterProductValues = DataCenterProducts[DataCenterProductKey];

export type ProductOffering<T extends CloudOfferingKey> =
    | (T extends "atlassianGuard"
          ? `${CloudOfferings[T]}-${Lowercase<
                Exclude<ProductPlanNames, "Enterprise">
            >}-sku`
          : `${CloudOfferings[T]}-${Lowercase<
                Exclude<ProductPlanNames, "Enterprise">
            >}`)
    | string;

export type OfferingSKU<P extends CloudOfferingKey> = ProductOffering<P>;

export type OfferingSKUMap<P extends CloudOfferingKey, T> = {
    [key in ProductOffering<P>]: T;
};

export type OfferingHamsMap<P extends CloudProductKey, T> = {
    [key in P]: T;
};

export const SpecialProductKeys = {
    JSM: "jira-servicedesk.ondemand",
    BITBUCKET: "com.atlassian.bitbucket.manual"
} as const;

export const DefaultPlanLimits = {
    UNIT_LIMIT_FREE: 10,
    UNIT_LIMIT: 150000,
    UNIT_MIN_ENTERPRISE: 801
} as const;

export type PriceCalculationRequest = {
    readonly ccpOfferingSKU?: OfferingSKU<CloudOfferingKey>;
    readonly hamsProductKey?: CloudProductKey;

    readonly numUnits: number;
    readonly period: PricingPeriod;
};

export type PriceCalculationResponse = {
    readonly totalPrice: number;
    readonly avgPerUnitPrice: number;

    readonly breakdown: BreakdownPriceTier[];

    readonly hasFlatRateFirstTier: boolean;
};

export type DiscountedPriceCalculationResponse = {
    readonly avgPerUnitPriceWithDiscount: number;
};

export type FormattedPriceCalculationResponse = {
    readonly formattedTotalPrice: string;
    readonly formattedAvgPerUnitPrice: string;
    readonly formattedAvgPerUnitPriceWithDiscount: string;
    readonly formattedTotalPriceWithDiscount: string;

    readonly formattedBreakdown: FormattedBreakdownPriceTier[];
} & PriceCalculationResponse &
    DiscountedPriceCalculationResponse;

export type BreakdownPriceTier = {
    unitsInTier: number;
    unitsAvailableInTier: number;

    unitLimitLower: number;
    unitLimitUpper: number;

    totalPriceForTier: number;
    avgPerUnitPriceForTier: number;
};

export type FormattedBreakdownPriceTier = {
    formattedUnitsInTier: string;
    formattedUnitsAvailableInTier: string;

    formattedUnitLimitLower: string;
    formattedUnitLimitUpper: string;

    formattedTotalPriceForTier: string;
    formattedAvgPerUnitPriceForTier: string;
};

export type PricingPeriod = "MONTHLY" | "ANNUAL";

export type PlanFeatureMessage = {
    featureMessage: LinkModalProps | string;
    lozengeText?: string;
    padded?: boolean;
};

/* Logic to construct URLs based on localization requirements and domain constraints:
 * 1. For URLs that belong to a different domain than WAC,
 * 2. If localization is not needed or the signup URL path differs from WAC products, use the standard path provided along with the domain
 * 3. For WAC products requiring localization, continue using the existing `signupURLSuffix` logic.
 */
export type PlanSignupURL = {
    shouldLocalize: boolean;
    urlDomain: string; //example: "https://admin.atlassian.com"
    urlPath: string; //example: "/o/create?from=atlassian-access"
};

export type ProductPlan = {
    planName: ProductPlanNames;
    offeringSku?: ProductOffering<CloudOfferingKey>;
    recommendationLimit: number;
    description?: string;
    signupLimit: number;
    signupURLSuffix?: string;
    signupURLInfo?: PlanSignupURL;
    planFeatureMessages?: PlanFeatureMessage[];
};

//TODO: Where should we hold these values? Used in Product Catalog as well
export type PricingTier = {
    readonly pricingStrategy: PricingStrategyValue;
    readonly unitDescription: string;
    readonly unitLimitLower: number | null;
    readonly unitLimitUpper: number | null;
    readonly prices: ReadonlyArray<PricingTierPrice>;
};

export type PricingTierPrice = {
    readonly currency: "USD" | "JPY";
    readonly unitPrice: number;
};

export type PriceFinderMessages = {
    headerText?: MessageDescriptor; //optional value for Spork Experiment
    subHeaderText?: MessageDescriptor; //optional value for Spork Experiment
    unitCountInputTextRedesign?: MessageDescriptor; //value for team24 redesign
    unitCountInputDirectBuy?: MessageDescriptor; // value for direct buy experiment
    unitCountInputTerm?: MessageDescriptor; //value for team24 redesign
    billingCycleText?: MessageDescriptor; //value for team24 redesign
    unitCountInputText?: MessageDescriptor;
    unitCountInputTooltip?: MessageDescriptor;
    cardOverSignupLimit?: MessageDescriptor;
    cardEnterpriseMinimumCount?: MessageDescriptor;
    cardUnitCountTierRange?: MessageDescriptor; // Needs to have a lowerLimit and upperLimit value
    cardMonthlyPerUnitCount?: MessageDescriptor;
    cardFreeDescription?: MessageDescriptor;
    cardEnterpriseBodyOverride?: ReactNode;
};

export const PricingStrategy = {
    FIXED_COST: "FIXED_COST",
    MARGINAL_PER_UNIT: "MARGINAL_PER_UNIT",
    PER_UNIT: "PER_UNIT"
} as const;

export type PricingStrategyValue =
    (typeof PricingStrategy)[keyof typeof PricingStrategy];

export function isPricingStrategy(value: PricingStrategyValue) {
    switch (value) {
        case PricingStrategy.FIXED_COST:
        case PricingStrategy.MARGINAL_PER_UNIT:
        case PricingStrategy.PER_UNIT:
            return true;
        default:
            return false;
    }
}

export function parsePricingStrategy(
    value: PricingStrategyValue
): PricingStrategyValue {
    if (isPricingStrategy(value)) {
        return value;
    } else {
        throw new SyntaxError(`${value} is not a valid PricingStrategy`);
    }
}

export type PricingPlan = ReadonlyArray<PricingTier>;
export type PricingPlans = {
    readonly monthly?: PricingPlan;
    readonly annual?: PricingPlan;
};

export const TRELLO_CTA_URL =
    "https://id.atlassian.com/signup?application=trello&continue=https%3A%2F%2Ftrello.com%2Fauth%2Fatlassian%2Fcallback%3Fdisplay%3DeyJ2ZXJpZmljYXRpb25TdHJhdGVneSI6InNvZnQifQ%253D%253D&display=eyJ2ZXJpZmljYXRpb25TdHJhdGVneSI6InNvZnQifQ%3D%3D";

export const SIGNUP_CTA_URL = "/try/cloud/signup";
export const CONTACT_CTA_URL = "/enterprise/contact?formType=pricing-quotes";

export const CtaBtnText = {
    GET_IT_NOW: "Get it now",
    START_FREE_TRIAL: "Start free trial",
    CONTACT_SALES: "Contact sales",
    JOIN_WAITLIST: "Join waitlist"
};

export const Edition = {
    FREE: "free",
    STANDARD: "standard",
    PREMIUM: "premium",
    ENTERPRISE: "enterprise"
};

export const PRICE_FINDER_TOP_SCROLL_THRESHOLD = 67;
export const PRICE_FINDER_BOTTOM_SCROLL_THRESHOLD = 210;
export const DESKTOP_SCREEN_WIDTH = 1024;

export const PRICE_FINDER_CONTAINER = "priceBlock";
export const COMPARE_FEATURES_CONTAINER = "priceFeaturesTable";
